import React, { useState } from 'react';
import { Report } from '../types';
import { Button } from './Button';
import { X, ArrowRight, AlertTriangle, CheckCircle2, Save, Copy, CheckSquare, Square, Check, RotateCcw } from 'lucide-react';

interface Props {
  isOpen: boolean;
  importData: Report; // Die Daten aus der Datei
  existingProject: any | null; // Das gefundene Projekt aus der DB (falls vorhanden)
  onConfirm: (action: 'create_new' | 'update', data: Report) => void;
  onClose: () => void;
}

// Definition der übernehmbaren Felder
const IMPORT_FIELDS = [
    { key: 'metadata.projectNumber', label: 'Projektnummer', category: 'Stammdaten' },
    { key: 'metadata.projectName', label: 'Projektname', category: 'Stammdaten' },
    { key: 'metadata.projectAddress', label: 'Standort / Adresse', category: 'Stammdaten' },
    { key: 'metadata.date', label: 'Begehungs-Datum', category: 'Stammdaten' },
    
    // Verantwortlichkeiten
    { key: 'metadata.clientName', label: 'Bauherr Name', category: 'Verantwortlichkeiten' },
    { key: 'metadata.clientAddress', label: 'Bauherr Anschrift', category: 'Verantwortlichkeiten' },
    { key: 'metadata.clientContactPerson', label: 'Bauherr Kontakt', category: 'Verantwortlichkeiten' },
    { key: 'metadata.coordinator', label: 'Koordinator', category: 'Verantwortlichkeiten' },
    { key: 'metadata.distributionList', label: 'E-Mail Verteiler / Beteiligte', category: 'Verantwortlichkeiten', isList: true },

    // Listen & Inhalte
    { key: 'items', label: 'Mängelliste (Alle Einträge)', category: 'Listen & Inhalte', isList: true },
    { key: 'checklists', label: 'Checklisten-Status', category: 'Listen & Inhalte', isList: true },
    { key: 'mutualHazards', label: 'Gefährdungs-Matrix', category: 'Listen & Inhalte', isList: true },
    { key: 'safetyPoints', label: 'Markierungen auf Plan', category: 'Listen & Inhalte', isList: true },
    { key: 'metadata.plans', label: 'Baupläne (Hinterlegte Bilder)', category: 'Listen & Inhalte', isList: true },
];

// Hilfsfunktion: Wert aus verschachteltem Objekt lesen
const getNestedValue = (obj: any, path: string) => {
    return path.split('.').reduce((prev, curr) => prev ? prev[curr] : undefined, obj);
};

// Initialisierung: Selektiere automatisch Felder, die unterschiedlich sind
const buildInitialSelection = (importData: Report, existingProject: any | null): Record<string, boolean> => {
    const initialSelection: Record<string, boolean> = {};
    if (!existingProject) return initialSelection;

    const dbData = existingProject.data;

    IMPORT_FIELDS.forEach(field => {
        const newVal = getNestedValue(importData, field.key);
        const oldVal = getNestedValue(dbData, field.key);

        // Einfacher Vergleich (JSON stringify für Objekte/Arrays)
        if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
            // Wenn Neu-Wert existiert, standardmäßig zum Update auswählen
            if (newVal !== undefined && newVal !== null && newVal !== '') {
                initialSelection[field.key] = true;
            }
        }
    });

    return initialSelection;
};

export const ImportPreviewModal: React.FC<Props> = ({ isOpen, importData, existingProject, onConfirm, onClose }) => {
  // Der Dialog wird nur gemountet, solange er offen ist – der Initial-State ist damit der Reset pro Öffnen.
  const [action, setAction] = useState<'create_new' | 'update'>(existingProject ? 'update' : 'create_new');

  // State für die Auswahl der Felder (Key -> Boolean)
  const [selectedFields, setSelectedFields] = useState<Record<string, boolean>>(() => buildInitialSelection(importData, existingProject));

  // Hilfsfunktion: Wert in verschachteltes Objekt schreiben
  const setNestedValue = (obj: any, path: string, value: any) => {
      const keys = path.split('.');
      let current = obj;
      for (let i = 0; i < keys.length - 1; i++) {
          if (!current[keys[i]]) current[keys[i]] = {};
          current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
  };

  const handleToggleField = (key: string) => {
      setSelectedFields(prev => ({
          ...prev,
          [key]: !prev[key]
      }));
  };

  const handleSelectAll = () => {
      const all: Record<string, boolean> = {};
      IMPORT_FIELDS.forEach(f => all[f.key] = true);
      setSelectedFields(all);
  };

  const handleSelectNone = () => {
      setSelectedFields({});
  };

  const handleConfirmAction = () => {
      if (action === 'create_new') {
          // Bei "Neu anlegen" nehmen wir einfach alles aus der Datei
          onConfirm('create_new', importData);
      } else {
          // Bei "Update" mischen wir die Daten
          if (!existingProject) return;

          // Tiefe Kopie des bestehenden Projekts als Basis
          const mergedReport = JSON.parse(JSON.stringify(existingProject.data));
          
          // Überschreibe nur die selektierten Felder
          Object.entries(selectedFields).forEach(([key, isSelected]) => {
              if (isSelected) {
                  const valToImport = getNestedValue(importData, key);
                  setNestedValue(mergedReport, key, valToImport);
              }
          });

          onConfirm('update', mergedReport);
      }
  };

  if (!isOpen) return null;

  // Render Helper Function (not a Component)
  const renderDiffRow = (field: typeof IMPORT_FIELDS[0]) => {
    const newVal = getNestedValue(importData, field.key);
    const oldVal = existingProject ? getNestedValue(existingProject.data, field.key) : undefined;
    
    const isSelected = selectedFields[field.key];
    const isDisabled = action === 'create_new'; // Im "Neu"-Modus immer alles an
    const isActive = isDisabled || isSelected;

    // Formatierung für Anzeige
    const formatVal = (val: any) => {
        if (val === undefined || val === null || val === '') return <span className="text-slate-300 italic">Leer</span>;
        if (field.isList && Array.isArray(val)) return <span className="font-mono text-xs">{val.length} Einträge</span>;
        if (typeof val === 'object') return <span className="font-mono text-xs">Objekt</span>;
        // Datum formatieren
        if (field.key.includes('date') && typeof val === 'string') {
            try { return new Date(val).toLocaleDateString('de-DE'); } catch { return val; }
        }
        return String(val);
    };

    const isDiff = JSON.stringify(newVal) !== JSON.stringify(oldVal);
    // Wenn gleich, zeigen wir es dezenter an
    const rowOpacity = isDiff ? 'opacity-100' : 'opacity-60';

    return (
      <div 
        key={field.key}
        className={`grid grid-cols-12 gap-4 py-3 border-b border-slate-100 last:border-0 text-sm items-center hover:bg-slate-50 transition-colors cursor-pointer ${rowOpacity}`}
        onClick={() => !isDisabled && handleToggleField(field.key)}
      >
        {/* Checkbox Spalte */}
        <div className="col-span-1 pl-2 flex justify-center">
             <div className={`transition-colors ${isDisabled ? 'text-slate-300' : (isActive ? 'text-blue-600' : 'text-slate-300')}`}>
                 {isActive ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5" />}
             </div>
        </div>

        {/* Label */}
        <div className="col-span-3 font-medium text-slate-600">
            {field.label}
        </div>

        {/* Alter Wert (DB) */}
        <div className={`col-span-3 text-slate-500 break-words pr-2 border-r border-slate-100 ${isActive ? 'line-through decoration-slate-300 opacity-70' : ''}`}>
           {existingProject ? formatVal(oldVal) : '-'}
        </div>
        
        {/* Pfeil */}
        <div className="col-span-1 flex justify-center items-center">
            <ArrowRight className={`w-4 h-4 ${isActive ? 'text-blue-500' : 'text-slate-300'}`} />
        </div>
        
        {/* Neuer Wert (Import) */}
        <div className={`col-span-4 font-bold break-words ${isActive ? 'text-blue-700' : 'text-slate-400'}`}>
           {formatVal(newVal)}
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col max-h-[90vh] overflow-hidden animate-scale-in">
        
        {/* Header */}
        <div className={`p-6 text-white flex justify-between items-center shrink-0 ${existingProject ? 'bg-indigo-600' : 'bg-emerald-600'}`}>
            <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                    {existingProject ? <AlertTriangle className="w-6 h-6"/> : <CheckCircle2 className="w-6 h-6"/>}
                </div>
                <div>
                    <h3 className="font-bold text-xl">Import Prüfung</h3>
                    <p className="opacity-90 text-sm">
                        {existingProject 
                            ? `Projekt "${existingProject.name}" existiert bereits.` 
                            : 'Neues Projekt wird angelegt.'}
                    </p>
                </div>
            </div>
            <button onClick={onClose} className="hover:bg-white/10 p-2 rounded-full transition-colors"><X className="w-6 h-6"/></button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 bg-slate-50">
            
            {/* Action Selection */}
            {existingProject && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <label className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${action === 'update' ? 'border-blue-500 bg-white shadow-md' : 'border-slate-200 bg-slate-50 hover:bg-white hover:border-blue-200'}`}>
                        <div className="mt-1">
                            <input 
                                type="radio" 
                                name="import-action" 
                                value="update" 
                                checked={action === 'update'} 
                                onChange={() => setAction('update')}
                                className="w-5 h-5 text-blue-600 focus:ring-blue-500"
                            />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 font-bold text-slate-800 text-base mb-1">
                                <Save className="w-5 h-5 text-blue-600"/>
                                Bestehendes Projekt aktualisieren
                            </div>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Wählen Sie unten aus, welche Felder überschrieben werden sollen. Nicht gewählte Felder bleiben im Original erhalten.
                            </p>
                        </div>
                    </label>

                    <label className={`flex items-start gap-4 p-4 rounded-xl border-2 cursor-pointer transition-all ${action === 'create_new' ? 'border-emerald-500 bg-white shadow-md' : 'border-slate-200 bg-slate-50 hover:bg-white hover:border-emerald-200'}`}>
                        <div className="mt-1">
                            <input 
                                type="radio" 
                                name="import-action" 
                                value="create_new" 
                                checked={action === 'create_new'} 
                                onChange={() => setAction('create_new')}
                                className="w-5 h-5 text-emerald-600 focus:ring-emerald-500"
                            />
                        </div>
                        <div>
                            <div className="flex items-center gap-2 font-bold text-slate-800 text-base mb-1">
                                <Copy className="w-5 h-5 text-emerald-600"/>
                                Als neues Projekt anlegen (Kopie)
                            </div>
                            <p className="text-xs text-slate-500 leading-relaxed">
                                Erstellt ein separates Duplikat. Das existierende Projekt bleibt unverändert. Alle Daten der Datei werden übernommen.
                            </p>
                        </div>
                    </label>
                </div>
            )}

            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
                <div className="bg-slate-50 p-3 border-b border-slate-200 flex justify-between items-center">
                    <div className="text-xs font-bold text-slate-500 uppercase tracking-wider pl-2">
                        Daten-Vergleich & Auswahl
                    </div>
                    {action === 'update' && (
                        <div className="flex gap-2">
                            <button onClick={handleSelectAll} className="text-[11px] flex items-center gap-1 bg-white border border-slate-300 px-2 py-1 rounded hover:text-blue-600 hover:border-blue-400 transition-colors">
                                <Check className="w-3 h-3"/> Alle
                            </button>
                            <button onClick={handleSelectNone} className="text-[11px] flex items-center gap-1 bg-white border border-slate-300 px-2 py-1 rounded hover:text-red-600 hover:border-red-400 transition-colors">
                                <RotateCcw className="w-3 h-3"/> Keine
                            </button>
                        </div>
                    )}
                </div>
                
                <div className="bg-slate-50 border-b border-slate-100 grid grid-cols-12 gap-4 py-2 px-4 text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    <div className="col-span-1 text-center">Übernehmen</div>
                    <div className="col-span-3">Feld</div>
                    <div className="col-span-3">Aktuell (DB)</div>
                    <div className="col-span-1 text-center"></div>
                    <div className="col-span-4">Neu (Datei)</div>
                </div>

                <div className="divide-y divide-slate-100 px-4">
                    {/* Stammdaten */}
                    <div className="py-2 text-xs font-bold text-slate-800 bg-slate-50/50 -mx-4 px-4 mt-2 mb-1">Stammdaten</div>
                    {IMPORT_FIELDS.filter(f => f.category === 'Stammdaten').map(renderDiffRow)}

                    {/* Verantwortlichkeiten */}
                    <div className="py-2 text-xs font-bold text-slate-800 bg-slate-50/50 -mx-4 px-4 mt-4 mb-1">Verantwortlichkeiten</div>
                    {IMPORT_FIELDS.filter(f => f.category === 'Verantwortlichkeiten').map(renderDiffRow)}

                    {/* Listen */}
                    <div className="py-2 text-xs font-bold text-slate-800 bg-slate-50/50 -mx-4 px-4 mt-4 mb-1">Listen & Inhalte</div>
                    {IMPORT_FIELDS.filter(f => f.category === 'Listen & Inhalte').map(renderDiffRow)}
                </div>
                <div className="h-4"></div>
            </div>

        </div>

        {/* Footer */}
        <div className="p-6 bg-white border-t border-slate-200 flex justify-between items-center shrink-0">
            <div className="text-xs text-slate-500">
                {action === 'update' 
                    ? <span><b>{Object.values(selectedFields).filter(Boolean).length}</b> Felder zum Aktualisieren ausgewählt.</span> 
                    : <span>Es wird ein <b>neues Projekt</b> erstellt (Alle Daten werden übernommen).</span>
                }
            </div>
            <div className="flex gap-3">
                <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirmAction}
                    className={existingProject && action === 'update' ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                >
                    {existingProject && action === 'update' ? 'Auswahl übernehmen' : 'Projekt importieren'}
                </Button>
            </div>
        </div>
      </div>
    </div>
  );
};